'use client';

import { useEffect, useState } from 'react';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import {
  DEFAULT_FINANCE_WORKFLOW_MODE,
  DEFAULT_PROCUREMENT_LIFECYCLE_MODE,
  DEFAULT_AUTO_PUBLISH_THRESHOLD,
  DEFAULT_EXPENSE_DOC_TYPE_MODE,
  DEFAULT_THREE_WAY_MATCH_TOLERANCE,
  DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT,
  DEFAULT_THREE_WAY_MATCH_AUTO_HOLD,
  DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH,
  DEFAULT_INVENTORY_TRACKING_ENABLED,
  DEFAULT_AUTO_EXTERNAL_PUBLISH_ENABLED,
  DEFAULT_AUTO_EXTERNAL_PUBLISH_ADAPTER,
  DEFAULT_AUTO_EXTERNAL_PUBLISH_MAX_AMOUNT,
  DEFAULT_EXTERNAL_SYNC_DIRECTION,
  DEFAULT_EXTERNAL_SYNC_MODE,
  DEFAULT_EXTERNAL_SYNC_FREQUENCY,
  DEFAULT_XERO_PUBLISH_STATUS,
  DEFAULT_AUTHORITY_MODE,
  DEFAULT_CONFLICT_RESOLUTION,
  DEFAULT_CONFLICT_WINDOW_SECONDS,
  resolveFinanceWorkflowMode,
  resolveProcurementLifecycleMode,
  resolveAutoPublishThreshold,
  resolveThreeWayMatchConfig,
  resolveInventoryTrackingEnabled,
  resolveExternalAutoPublishConfig,
  resolveExternalSyncDirection,
  resolveExternalSyncMode,
  resolveExternalSyncFrequency,
  resolveXeroPublishStatus,
  resolveExternalSyncAuthority,
  getDataTypeAuthority,
  isPublishAllowed,
  resolveEnabledDocumentTypes,
  resolveExpenseDocTypeMode,
  resolvePublishDateSource,
  DEFAULT_PUBLISH_DATE_SOURCE,
  type FinanceWorkflowMode,
  type ProcurementLifecycleMode,
  type ExpenseDocTypeMode,
  type PublishDateSource,
  type ThreeWayMatchConfig,
  type ExternalAutoPublishConfig,
  type ExternalSyncDirection,
  type ExternalSyncMode,
  type ExternalSyncFrequency,
  type XeroPublishStatus,
  type ExternalSyncAuthority,
  type DataTypeAuthority,
} from '@/lib/finance/workflowMode';

export interface FinanceSettings {
  workflowMode: FinanceWorkflowMode;
  procurementLifecycleMode: ProcurementLifecycleMode;
  autoPublishThreshold: number;
  threeWayMatchConfig: ThreeWayMatchConfig;
  inventoryTrackingEnabled: boolean;
  externalAutoPublish: ExternalAutoPublishConfig;
  /** 'auto' = COD→EXP / terms→BIL; 'always_supplier_invoice' = all expenses → BIL */
  expenseDocTypeMode: ExpenseDocTypeMode;
  /** Which date lands on external documents: 'intake_date' (issue/intake) or 'transaction_date' (slip date) */
  publishDateSource: PublishDateSource;
  externalSyncDirection: ExternalSyncDirection;
  externalSyncMode: ExternalSyncMode;
  externalSyncFrequency: ExternalSyncFrequency;
  externalSyncAuthority: ExternalSyncAuthority;
  xeroPublishStatus: XeroPublishStatus;
  /** Extraction confidence threshold (0-100): docs below this are flagged for review */
  extractionConfidenceThreshold: number;
  /** Client-enabled document types (empty = all subscription-allowed types enabled) */
  enabledDocumentTypes: Record<string, boolean>;
  /** Number of approval levels configured in HubSettings (1-5). Used to
   *  control Pending Approval tab visibility in standard workflow mode. */
  approvalLevels: number;
  loading: boolean;
}

const DEFAULT_EXTERNAL_AUTO_PUBLISH: ExternalAutoPublishConfig = {
  enabled: DEFAULT_AUTO_EXTERNAL_PUBLISH_ENABLED,
  adapterId: DEFAULT_AUTO_EXTERNAL_PUBLISH_ADAPTER,
  docTypes: {},
  maxAmount: DEFAULT_AUTO_EXTERNAL_PUBLISH_MAX_AMOUNT,
};

const DEFAULT_SETTINGS: FinanceSettings = {
  workflowMode: DEFAULT_FINANCE_WORKFLOW_MODE,
  procurementLifecycleMode: DEFAULT_PROCUREMENT_LIFECYCLE_MODE,
  autoPublishThreshold: DEFAULT_AUTO_PUBLISH_THRESHOLD,
  threeWayMatchConfig: {
    tolerance: DEFAULT_THREE_WAY_MATCH_TOLERANCE,
    tolerancePct: DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT,
    autoHold: DEFAULT_THREE_WAY_MATCH_AUTO_HOLD,
    partialMatch: DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH,
  },
  inventoryTrackingEnabled: DEFAULT_INVENTORY_TRACKING_ENABLED,
  externalAutoPublish: { ...DEFAULT_EXTERNAL_AUTO_PUBLISH },
  expenseDocTypeMode: DEFAULT_EXPENSE_DOC_TYPE_MODE,
  publishDateSource: DEFAULT_PUBLISH_DATE_SOURCE,
  externalSyncDirection: DEFAULT_EXTERNAL_SYNC_DIRECTION,
  externalSyncMode: DEFAULT_EXTERNAL_SYNC_MODE,
  externalSyncFrequency: DEFAULT_EXTERNAL_SYNC_FREQUENCY,
  externalSyncAuthority: {
    mode: DEFAULT_AUTHORITY_MODE,
    data_type_authority: {},
    conflict_resolution: DEFAULT_CONFLICT_RESOLUTION,
    conflict_window_seconds: DEFAULT_CONFLICT_WINDOW_SECONDS,
  },
  xeroPublishStatus: DEFAULT_XERO_PUBLISH_STATUS,
  extractionConfidenceThreshold: 70,
  enabledDocumentTypes: {},
  approvalLevels: 1,
  loading: true,
};

// ── Cross-component cache ───────────────────────────────────────────────────
// Multiple components often use useFinanceSettings simultaneously. Without a
// cache, each instance fires its own GET /api/v1/finance/settings. This simple
// promise cache ensures only one network request is in-flight at a time, and
// subsequent mounts reuse the resolved data within the cache TTL.

const CACHE_TTL_MS = 10_000; // 10 seconds — reduced from 30s to avoid stale workflow mode

type CachedSettings = {
  data: Omit<FinanceSettings, 'loading'>;
  fetchedAt: number;
};

let cachedSettings: CachedSettings | null = null;
let inFlightPromise: Promise<Omit<FinanceSettings, 'loading'>> | null = null;

/**
 * Resolve the configured number of approval levels from HubSettings.
 * Stored under `approval_workflows.approval_levels` in custom_settings.
 * Defaults to 1 (single approval) when unset or invalid.
 */
function resolveApprovalLevels(customSettings: unknown): number {
  const cs = (customSettings && typeof customSettings === 'object' && !Array.isArray(customSettings))
    ? customSettings as Record<string, unknown>
    : {};
  const approvalWorkflows = cs.approval_workflows;
  const aw = (approvalWorkflows && typeof approvalWorkflows === 'object' && !Array.isArray(approvalWorkflows))
    ? approvalWorkflows as Record<string, unknown>
    : {};
  const raw = aw.approval_levels;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1 && raw <= 5) {
    return Math.floor(raw);
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 5) {
      return parsed;
    }
  }
  return 1;
}

function resolveSettingsFromJson(json: unknown): Omit<FinanceSettings, 'loading'> {
  const customSettings = (json as { data?: { custom_settings?: unknown } })?.data?.custom_settings;
  const cs = (customSettings && typeof customSettings === 'object' && !Array.isArray(customSettings))
    ? customSettings as Record<string, unknown>
    : {};
  return {
    workflowMode: resolveFinanceWorkflowMode(customSettings),
    procurementLifecycleMode: resolveProcurementLifecycleMode(customSettings),
    autoPublishThreshold: resolveAutoPublishThreshold(customSettings),
    threeWayMatchConfig: resolveThreeWayMatchConfig(customSettings),
    inventoryTrackingEnabled: resolveInventoryTrackingEnabled(customSettings),
    externalAutoPublish: resolveExternalAutoPublishConfig(customSettings),
    expenseDocTypeMode: resolveExpenseDocTypeMode(customSettings),
    publishDateSource: resolvePublishDateSource(customSettings),
    externalSyncDirection: resolveExternalSyncDirection(customSettings),
    externalSyncMode: resolveExternalSyncMode(customSettings),
    externalSyncFrequency: resolveExternalSyncFrequency(customSettings),
    externalSyncAuthority: resolveExternalSyncAuthority(customSettings),
    xeroPublishStatus: resolveXeroPublishStatus(customSettings),
    extractionConfidenceThreshold: typeof cs.extraction_confidence_threshold === 'number'
      ? Math.max(0, Math.min(100, cs.extraction_confidence_threshold))
      : 70,
    enabledDocumentTypes: resolveEnabledDocumentTypes(customSettings),
    approvalLevels: resolveApprovalLevels(customSettings),
  };
}

/** Shared fallback used when the API is unreachable or returns an error. */
const FALLBACK_SETTINGS: Omit<FinanceSettings, 'loading'> = {
  workflowMode: DEFAULT_FINANCE_WORKFLOW_MODE,
  procurementLifecycleMode: DEFAULT_PROCUREMENT_LIFECYCLE_MODE,
  autoPublishThreshold: DEFAULT_AUTO_PUBLISH_THRESHOLD,
  threeWayMatchConfig: {
    tolerance: DEFAULT_THREE_WAY_MATCH_TOLERANCE,
    tolerancePct: DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT,
    autoHold: DEFAULT_THREE_WAY_MATCH_AUTO_HOLD,
    partialMatch: DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH,
  },
  inventoryTrackingEnabled: DEFAULT_INVENTORY_TRACKING_ENABLED,
  externalAutoPublish: { ...DEFAULT_EXTERNAL_AUTO_PUBLISH },
  expenseDocTypeMode: DEFAULT_EXPENSE_DOC_TYPE_MODE,
  publishDateSource: DEFAULT_PUBLISH_DATE_SOURCE,
  externalSyncDirection: DEFAULT_EXTERNAL_SYNC_DIRECTION,
  externalSyncMode: DEFAULT_EXTERNAL_SYNC_MODE,
  externalSyncFrequency: DEFAULT_EXTERNAL_SYNC_FREQUENCY,
  externalSyncAuthority: {
    mode: DEFAULT_AUTHORITY_MODE,
    data_type_authority: {},
    conflict_resolution: DEFAULT_CONFLICT_RESOLUTION,
    conflict_window_seconds: DEFAULT_CONFLICT_WINDOW_SECONDS,
  },
  xeroPublishStatus: DEFAULT_XERO_PUBLISH_STATUS,
  extractionConfidenceThreshold: 70,
  enabledDocumentTypes: {},
  approvalLevels: 1,
};

async function fetchSettings(): Promise<Omit<FinanceSettings, 'loading'>> {
  const token = authService.getTokens()?.access_token;
  if (!token) {
    return { ...FALLBACK_SETTINGS };
  }

  const response = await fetch('/api/v1/finance/settings', {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json().catch(() => null);

  if (!json?.success) {
    return { ...FALLBACK_SETTINGS };
  }

  return resolveSettingsFromJson(json);
}

function getSettings(): Promise<Omit<FinanceSettings, 'loading'>> {
  // Return cached data if still fresh
  if (cachedSettings && Date.now() - cachedSettings.fetchedAt < CACHE_TTL_MS) {
    return Promise.resolve(cachedSettings.data);
  }

  // Deduplicate concurrent fetches
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = fetchSettings()
    .then((data) => {
      cachedSettings = { data, fetchedAt: Date.now() };
      return data;
    })
    .catch(() => ({ ...FALLBACK_SETTINGS }))
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
}

/** Invalidate the cache so the next mount refetches from the API. */
export function invalidateFinanceSettingsCache(): void {
  cachedSettings = null;
}

/**
 * Consolidated hook that fetches finance settings once and returns both
 * workflowMode and procurementLifecycleMode. This replaces the separate
 * useFinanceWorkflowMode and useProcurementLifecycleMode hooks which each
 * independently GET /api/v1/finance/settings, causing duplicate network calls.
 *
 * Uses a module-level cache so multiple components sharing this hook reuse
 * the same fetch result within a 30-second TTL window.
 */
export function useFinanceSettings(enabled = true): FinanceSettings {
  const [settings, setSettings] = useState<FinanceSettings>({
    ...DEFAULT_SETTINGS,
    loading: enabled,
  });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    void (async () => {
      try {
        setSettings((prev) => ({ ...prev, loading: true }));
        const data = await getSettings();
        if (!cancelled) {
          setSettings({ ...data, loading: false });
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('[useFinanceSettings] Failed to load finance settings:', error);
        if (!cancelled) {
          setSettings({
            ...FALLBACK_SETTINGS,
            loading: false,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Invalidate cache on window focus so admin workflow-mode changes are
  // picked up without waiting for the TTL to expire.
  useEffect(() => {
    if (!enabled) return;
    const handleFocus = () => {
      cachedSettings = null;
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        cachedSettings = null;
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled]);

  return settings;
}

/**
 * Convenience hook for sub-modules to check their publish authority.
 *
 * Returns whether this sub-module is allowed to publish to the external system,
 * plus the authority mode and a human-readable label for UI display.
 *
 * Usage:
 *   const { canPublish, authorityLabel } = usePublishAuthority('expense_documents');
 *   if (!canPublish) return <DisabledState reason="QBO is master for expenses" />
 */
export function usePublishAuthority(dataType: DataTypeAuthority): {
  canPublish: boolean;
  canImport: boolean;
  authority: 'client' | 'external' | 'co_master';
  mode: ExternalSyncAuthority['mode'];
  authorityLabel: string;
} {
  const { externalSyncAuthority } = useFinanceSettings();
  const authority = getDataTypeAuthority(
    { external_sync_authority: externalSyncAuthority },
    dataType,
  );
  return {
    canPublish: authority === 'client' || authority === 'co_master',
    canImport: authority === 'external' || authority === 'co_master',
    authority,
    mode: externalSyncAuthority.mode,
    authorityLabel:
      authority === 'client' ? 'TF Master'
      : authority === 'co_master' ? 'Co-Master'
      : 'QBO Master',
  };
}

export default useFinanceSettings;
